// ============================================
// FILE: /frontend/src/pages/CompareListingsPage.jsx
// PURPOSE: Compare listings side-by-side - IMAGES FIXED
// ============================================

import { useState, useEffect } from 'react';
import { GitCompare, Loader2, X, ArrowRight } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/useToast';
import { compareAPI } from '../api/compare.api';
import { useNavigate } from 'react-router-dom';

export default function CompareListingsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareList, setCompareList] = useState([]);
  const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    loadCompareList();
  }, []);

  const loadCompareList = async () => {
    try {
      const saved = localStorage.getItem('compareListings');
      if (!saved) {
        setLoading(false);
        return;
      }

      const listingIds = JSON.parse(saved);
      setCompareList(listingIds);

      if (listingIds.length > 0) {
        const response = await compareAPI.compareListings(listingIds);
        setListings(response.listings);
      }
    } catch (error) {
      console.error('Failed to load comparison:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load comparison.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveListing = (listingId) => {
    const updatedList = compareList.filter((id) => id !== listingId);
    setCompareList(updatedList);
    setListings(listings.filter((l) => l.id !== listingId));
    localStorage.setItem('compareListings', JSON.stringify(updatedList));
    window.dispatchEvent(new Event('compareListUpdated'));

    toast({
      title: 'Removed',
      description: 'Listing removed from comparison.',
    });
  };

  const handleClearAll = () => {
    setCompareList([]);
    setListings([]);
    localStorage.removeItem('compareListings');
    window.dispatchEvent(new Event('compareListUpdated'));
    
    toast({
      title: 'Cleared',
      description: 'All listings removed from comparison.',
    });
  };

  // Helper function to get image URL
  const getImageUrl = (listing) => {
    let imageUrl = listing.imageUrl;
    
    if (Array.isArray(imageUrl) && imageUrl.length > 0) {
      imageUrl = imageUrl[0];
    } else if (listing.photos && listing.photos.length > 0) {
      imageUrl = listing.photos[0];
    }
    
    if (imageUrl && typeof imageUrl === 'string') {
      return imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`;
    }
    
    return '/placeholder-car.jpg';
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader className="p-4 sm:p-5 md:p-6 flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
                <GitCompare className="w-5 h-5 sm:w-6 sm:h-6" />
                Compare Listings ({listings.length})
              </CardTitle>
              {listings.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-8 sm:h-9 text-xs sm:text-sm"
                >
                  Clear All
                </Button>
              )}
            </CardHeader>

            <CardContent className="p-3 sm:p-4 md:p-6">
              {listings.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <GitCompare className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">
                    No Listings to Compare
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                    Add listings to comparison from listing cards
                  </p>
                  <Button onClick={() => navigate('/vehicles')} className="h-9 sm:h-10 text-xs sm:text-sm">
                    Browse Listings
                  </Button>
                </div>
              ) : (
                <>
                  {/* Mobile View - Grid of Cards */}
                  <div className="block lg:hidden">
                    <div className="grid grid-cols-2 gap-2">
                      {listings.map((listing) => (
                        <Card key={listing.id} className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveListing(listing.id)}
                            className="absolute top-1 right-1 h-6 w-6 p-0 z-10 bg-white/80 hover:bg-white"
                          >
                            <X className="w-3 h-3" />
                          </Button>

                          <CardContent className="p-2">
                            {/* Image */}
                            <div className="w-full aspect-square bg-gray-200 rounded mb-2">
                              <img
                                src={getImageUrl(listing)}
                                alt={listing.title}
                                className="w-full h-full object-cover rounded"
                                onError={(e) => {
                                  e.target.src = '/placeholder-car.jpg';
                                }}
                              />
                            </div>

                            {/* Compact Details */}
                            <div className="space-y-1">
                              <p className="font-semibold text-xs line-clamp-1">{listing.title}</p>
                              <p className="font-bold text-primary text-sm">
                                KES {parseFloat(listing.price).toLocaleString()}
                              </p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {listing.county}
                              </p>
                              {listing.type && (
                                <p className="text-[10px] text-muted-foreground truncate">
                                  {listing.type}
                                </p>
                              )}
                              {listing.make && (
                                <p className="text-[10px] text-muted-foreground truncate">
                                  {listing.make} {listing.model}
                                </p>
                              )}
                            </div>

                            <Button
                              onClick={() => navigate(`/listing/${listing.id}`)}
                              className="w-full mt-2 h-7 text-[10px]"
                              size="sm"
                            >
                              View
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Desktop View - Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-sm font-semibold bg-gray-50 sticky left-0 z-10">
                            Feature
                          </th>
                          {listings.map((listing) => (
                            <th key={listing.id} className="p-3 text-center relative min-w-[200px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveListing(listing.id)}
                                className="absolute top-1 right-1 h-6 w-6 p-0"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                              <div className="w-32 h-32 mx-auto bg-gray-200 rounded mb-2">
                                <img
                                  src={getImageUrl(listing)}
                                  alt={listing.title}
                                  className="w-full h-full object-cover rounded"
                                  onError={(e) => {
                                    e.target.src = '/placeholder-car.jpg';
                                  }}
                                />
                              </div>
                              <Button
                                onClick={() => navigate(`/listing/${listing.id}`)}
                                size="sm"
                                className="mt-2 text-xs"
                              >
                                View Details
                              </Button>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Title</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              {listing.title}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-primary/5">
                          <td className="p-3 font-medium text-sm bg-primary/10 sticky left-0">Price</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center">
                              <span className="font-bold text-primary text-base">
                                KES {parseFloat(listing.price).toLocaleString()}
                              </span>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">County</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              {listing.county}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Sub-County</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              {listing.subCounty}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Category</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              {listing.category}
                            </td>
                          ))}
                        </tr>
                        {listings[0]?.type && (
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Type</td>
                            {listings.map((listing) => (
                              <td key={listing.id} className="p-3 text-center text-sm">
                                {listing.type || '-'}
                              </td>
                            ))}
                          </tr>
                        )}
                        {listings[0]?.make && (
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Make</td>
                            {listings.map((listing) => (
                              <td key={listing.id} className="p-3 text-center text-sm">
                                {listing.make || '-'}
                              </td>
                            ))}
                          </tr>
                        )}
                        {listings[0]?.model && (
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Model</td>
                            {listings.map((listing) => (
                              <td key={listing.id} className="p-3 text-center text-sm">
                                {listing.model || '-'}
                              </td>
                            ))}
                          </tr>
                        )}
                        {listings[0]?.subType && (
                          <tr className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Year</td>
                            {listings.map((listing) => (
                              <td key={listing.id} className="p-3 text-center text-sm">
                                {listing.subType || '-'}
                              </td>
                            ))}
                          </tr>
                        )}
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Status</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              <span className={`inline-block px-2 py-1 rounded text-xs ${
                                listing.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {listing.status}
                              </span>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm bg-gray-50 sticky left-0">Posted</td>
                          {listings.map((listing) => (
                            <td key={listing.id} className="p-3 text-center text-sm">
                              {new Date(listing.createdAt).toLocaleDateString()}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}